import React, { useMemo } from 'react';
import 'styles/ant-design.css';
import ExcelTable from 'components/ui/excel-table';
import FormSection from 'components/ui/form-section';
import { useTranslation } from 'react-i18next';
import {
  ECellValueType,
  ExcelTableColDef,
} from 'components/ui/excel-table/types';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import { premiumScheduleApi } from 'services/apis';

interface BalanceTableProps {
  currency?: string;
  contractId?: string;
}

const BalanceTable: React.FC<BalanceTableProps> = ({
  contractId,
  currency,
}) => {
  const { data: balanceData, isLoading: isLoadingBalance } = useQuery(
    [EQueryKeys.CONTRACT_BALANCE, contractId, currency],
    async () => {
      if (!contractId) {
        return undefined;
      }
      return premiumScheduleApi.getContractBalance({
        contractId,
        currency,
      });
    },
    {
      enabled: !!contractId,
    }
  );

  const { t } = useTranslation();

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        headerName: t('premiumProcessing.shareOfPremiumSigned'),
        field: 'shareOfPremiumSigned',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        flex: 1,
        minWidth: 180,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.gstOnPremium'),
        field: 'gstOnPremium',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.cedingCommission'),
        field: 'cedingCommission',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        flex: 1,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.gstOnCedingCommission'),
        field: 'gstOnCedingCommission',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        flex: 1,
        minWidth: 180,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.sourceBrokerage'),
        field: 'sourceBrokerage',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        flex: 1,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: 'GST on Source Brokerage',
        field: 'gstOnSourceBrokerage',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        flex: 1,
        minWidth: 180,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.ere'),
        field: 'ere',
        minWidth: 120,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        resizable: true,
        editable: false,
        flex: 1,
      },
      {
        headerName: t('premiumProcessing.withholdingTax'),
        field: 'withholdingTax',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.netPremiumDueFromBroker'),
        field: 'netPremiumDueFromBroker',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.pinnacleCommission'),
        field: 'pinnacleCommission',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 170,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.ait'),
        field: 'ait',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 120,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.taxesAndFees'),
        field: 'taxesAndFees',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 150,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.binderCashRemittance'),
        field: 'binderCashRemittance',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        minWidth: 180,
        resizable: true,
        editable: false,
      },
    ],
    [t]
  );

  return (
    <>
      <FormSection title={t('premiumProcessing.balance')} collapsible>
        <ExcelTable
          data={balanceData ? [balanceData] : []}
          columnDefs={columnDefs}
          includeIdRow={false}
          tableContainerClassName="h-[12rem]"
          loading={isLoadingBalance}
        />
      </FormSection>
    </>
  );
};

export default BalanceTable;
